import { useEffect, useRef, useState, type ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import { TaskStatus } from "@/types";
import { Button } from "@/components/ui/button";
import { DeadlineField } from "@/components/tasks/DeadlineField";
import { getNextStatus, StatusCheckmark } from "@/components/tasks/StatusCheckmark";
import { SparkBurst, SPARK_MS, useCelebration } from "@/components/tasks/CompletionEffects";
import { DateIcon, DeleteIcon, EditIcon, CloseIcon } from "@/lib/icons";
import { formatDateFi } from "@/lib/format";
import { cn } from "@/lib/utils";
import { isMemoTaskOverdue, type MemoTask } from "@/lib/memoList";
import type { MemoTaskMutationResult, UpdateMemoTaskInput } from "@/hooks/useMemoLists";

/**
 * Määräaika picker in a popover: `DeadlineField` plus a `Poista määräaika`
 * button when a date is set. `onChange(null)` means the date was removed.
 * The panel portals to the document body: inside the dialog, its transform
 * and `overflow-hidden` would offset and clip the calendar.
 */
export function MemoDeadlinePopover({
  value,
  onChange,
  trigger,
  triggerClassName,
  triggerLabel,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  trigger: ReactNode;
  triggerClassName?: string;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);

  const choose = (next: string | null) => {
    setOpen(false);
    onChange(next);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          title={triggerLabel}
          className={cn(
            "flex items-center gap-1 rounded-md px-1.5 py-1 hover:bg-accent transition-colors",
            triggerClassName,
          )}
        >
          {trigger}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          side="bottom"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto rounded-md border bg-popover p-3 shadow-md"
        >
          <DeadlineField value={value ?? ""} onChange={(v) => choose(v)} />
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => choose(null)}
              className="mt-2 w-full text-muted-foreground hover:text-foreground"
            >
              <CloseIcon className="h-3.5 w-3.5 mr-1" />
              Poista määräaika
            </Button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * One Muistilista tehtävä in the `SubtaskItem` row style. The status cycles
 * through `getNextStatus` with the same optimistic click queue `SubtaskItem`
 * uses, so fast clicks each send the version the previous one returned.
 */
export function MemoTaskRow({
  task,
  onUpdate,
  onDelete,
  onEdit,
}: {
  task: MemoTask;
  onUpdate: (input: UpdateMemoTaskInput) => Promise<MemoTaskMutationResult>;
  onDelete: (task: MemoTask) => void;
  onEdit: (task: MemoTask) => void;
}) {
  const [justCompleted, setJustCompleted] = useState(false);
  const [sparkRun, fireSpark] = useCelebration(SPARK_MS);
  const [displayStatus, setDisplayStatus] = useState(task.status);
  const versionRef = useRef(task.version);
  const confirmedStatusRef = useRef(task.status);
  const queueRef = useRef<TaskStatus[]>([]);
  const drainingRef = useRef(false);

  useEffect(() => {
    if (!drainingRef.current && queueRef.current.length === 0) {
      setDisplayStatus(task.status);
      versionRef.current = task.version;
      confirmedStatusRef.current = task.status;
    }
  }, [task.status, task.version]);

  const canEdit = task.viewerCanEdit;
  const isCompleted = displayStatus === TaskStatus.COMPLETED;
  const overdue = isMemoTaskOverdue({ dueDate: task.dueDate, status: displayStatus }, new Date());
  const addedByOther =
    !!task.createdByUserId && task.createdByUserId !== task.ownerUserId && !!task.createdByName;

  const drainQueue = async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const target = queueRef.current[0];
        const result = await onUpdate({
          id: task.id,
          version: versionRef.current,
          status: target,
        });
        queueRef.current.shift();
        const newVersion = result.success ? result.memoTask?.version : undefined;
        if (typeof newVersion === "number") {
          versionRef.current = newVersion;
          confirmedStatusRef.current = target;
        } else {
          queueRef.current = [];
          setDisplayStatus(confirmedStatusRef.current);
          break;
        }
      }
    } finally {
      drainingRef.current = false;
    }
  };

  const handleStatusClick = () => {
    if (!canEdit) return;
    const base =
      queueRef.current.length > 0
        ? queueRef.current[queueRef.current.length - 1]
        : displayStatus;
    const nextStatus = getNextStatus(base);
    if (nextStatus === TaskStatus.COMPLETED && base !== TaskStatus.COMPLETED) {
      setJustCompleted(true);
      setTimeout(() => setJustCompleted(false), 700);
      fireSpark();
    }
    setDisplayStatus(nextStatus);
    queueRef.current.push(nextStatus);
    void drainQueue();
  };

  const handleDeadlineChange = async (next: string | null) => {
    const result = await onUpdate(
      next === null
        ? { id: task.id, version: versionRef.current, clearDueDate: true }
        : { id: task.id, version: versionRef.current, dueDate: next },
    );
    if (result.success && typeof result.memoTask?.version === "number") {
      versionRef.current = result.memoTask.version;
    }
  };

  const deadlineContent = task.dueDate ? (
    <span
      className={cn(
        "flex items-center gap-1 text-xs",
        overdue ? "font-medium text-red-600" : "text-muted-foreground",
      )}
    >
      <DateIcon className="h-3 w-3 shrink-0" />
      <span>{formatDateFi(task.dueDate)}</span>
    </span>
  ) : (
    <DateIcon className="h-3.5 w-3.5 text-muted-foreground" />
  );

  return (
    <div
      className={cn(
        "flex items-start gap-3 py-2.5 px-3 rounded-md border border-border/60 bg-card shadow-sm hover:shadow hover:border-border transition-all group",
        justCompleted && "motion-safe:animate-[flashGreen_0.7s_ease-out]",
      )}
    >
      <div className="relative mt-0.5">
        <SparkBurst runId={sparkRun} className="z-10" />
        <StatusCheckmark
          status={displayStatus}
          onClick={handleStatusClick}
          justCompleted={justCompleted}
          startedAt={task.startedAt}
          completedAt={task.completedAt}
          disabled={!canEdit}
        />
      </div>
      <div className="flex-1 min-w-0">
        <span
          className={cn(
            "text-sm block transition-all duration-300",
            isCompleted && "line-through text-muted-foreground",
          )}
        >
          {task.title}
        </span>
        {addedByOther && (
          <span className="text-xs text-muted-foreground block mt-0.5">
            lisännyt: {task.createdByName}
          </span>
        )}
        {task.description && (
          <span className="text-xs text-muted-foreground block mt-1">
            {task.description}
          </span>
        )}
      </div>
      <div className="shrink-0">
        {canEdit ? (
          <MemoDeadlinePopover
            value={task.dueDate}
            onChange={(next) => void handleDeadlineChange(next)}
            trigger={deadlineContent}
            triggerLabel={task.dueDate ? undefined : "Ei määräaikaa"}
          />
        ) : (
          task.dueDate && <div className="px-1.5 py-1">{deadlineContent}</div>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0">
          <button
            type="button"
            onClick={() => onEdit(task)}
            className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Muokkaa"
          >
            <EditIcon className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(task)}
            className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Poista"
          >
            <DeleteIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
